import React, { useEffect, useState } from "react";
import { Form, Row, Col, Badge } from "react-bootstrap";
import { Trans, useTranslation } from "react-i18next";
import { Flatpickr } from "components";
import { toast } from "react-toastify";
import { getLocations } from "api";
import Select from "react-select";
import moment from "moment";

interface Props {
  meeting: IMeeting;
  onChange: (meetingChanged: IMeeting) => void;
  editMode: boolean;
}

interface Location {
  mapid: string;
  name: string;
  type: string;
}

/**
 * Form for general information about the meeting.
 */
const MeetingInfoForm: React.FC<Props> = ({ meeting, onChange, editMode }) => {
  const { t } = useTranslation();
  const todayMidnight = new Date();
  todayMidnight.setHours(0, 0, 0, 0);
  const [buildings, setBuildings] = useState<Location[]>([]);
  const [rooms, setRooms] = useState<Location[]>([]);
  const [mapApiError, setMapApiError] = useState(false);

  const fetchLocations = async function () {
    const response = await getLocations();
    if (response.success) {
      const buildings = response.data.locations
        .filter(
          (location: Location) =>
            location.type === "BUILDING" || location.type === "GATE",
        )
        .sort((a: Location, b: Location) => {
          if (a.mapid > b.mapid) return 1;
          if (a.mapid < b.mapid) return -1;
          return 0;
        });
      setBuildings([...buildings]);
      setRooms(
        response.data.locations.filter(
          (location: Location) => location.type === "ROOM",
        ),
      );
    } else {
      toast.error(t(response.error));
      setMapApiError(true);
    }
  };

  useEffect(() => {
    fetchLocations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    /*
     * Maintain valid meeting time range in edit mode:
     * - End must be after start; default to one hour (end > start + 1 hour).
     * - The overall range must be at most 5 days (end <= start + 5 days).
     */
    if (
      editMode &&
      meeting.start &&
      (!meeting.end ||
        moment(meeting.end).isSameOrBefore(meeting.start) ||
        moment(meeting.end).isAfter(moment(meeting.start).add(5, "days")))
    ) {
      const start = moment(meeting.start);
      const minEnd = start.clone().add(1, "hours");
      const maxEnd = start.clone().add(5, "days");

      // Prefer the existing end when present, otherwise default to start +1 hour.
      const proposedEnd = meeting.end ? moment(meeting.end) : minEnd.clone();
      // Then clamp into [minEnd, maxEnd].
      const nextEnd = moment.min(moment.max(proposedEnd, minEnd), maxEnd);

      onChange({ ...meeting, end: nextEnd.toISOString() });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onChange, meeting.start, meeting.end, editMode]);

  return (
    <Row className="mb-2">
      <Col>
        <h4>{t("invitation.info")}</h4>
        <Row>
          <Col md="4">
            <Form.Group>
              <Form.Label>{t("invitation.building")}</Form.Label>
              {!mapApiError && editMode && (
                <Select
                  options={buildings}
                  isLoading={!buildings.length}
                  isClearable={true}
                  getOptionLabel={(option) => option.name}
                  getOptionValue={(option) => option.name}
                  isDisabled={!editMode}
                  placeholder="Building 20 (Product Center)"
                  value={{ name: meeting.building || "", mapid: "", type: "" }}
                  onChange={(location) => {
                    onChange({
                      ...meeting,
                      building: location ? (location as Location).name : "",
                    });
                  }}
                />
              )}
              {(mapApiError || !editMode) && (
                <input
                  disabled={!editMode}
                  className="form-control"
                  placeholder="Building 20 (Product Center)"
                  value={meeting.building || ""}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    onChange({ ...meeting, building: e.target.value })
                  }
                />
              )}
            </Form.Group>
          </Col>
          <Col md="4">
            <Form.Group>
              <Form.Label>{t("invitation.room")}</Form.Label>
              <input
                disabled={!editMode}
                list="rooms"
                className="form-control"
                placeholder={t("invitation.room_placeholder")}
                value={meeting.room || ""}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  onChange({ ...meeting, room: e.target.value })
                }
              />
              <datalist id="rooms">
                {rooms.sort &&
                  rooms
                    .sort()
                    .map((room: Location) => (
                      <option key={room.mapid}>{room.name}</option>
                    ))}
              </datalist>
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col md="4">
            <Form.Group>
              <Form.Label>{t("invitation.start")} *</Form.Label>
              <Flatpickr
                required
                disabled={!editMode}
                className="form-control"
                data-enable-time
                placeholder="YYYY-MM-DD HH:MM"
                value={meeting.start ? new Date(meeting.start) : undefined}
                options={{
                  dateFormat: "Y-m-d H:i",
                  allowInput: true,
                  minDate: meeting.start
                    ? moment(meeting.start).isSameOrAfter(moment(), "day")
                      ? todayMidnight // any date from today if start is defined and in the present or future
                      : meeting.start // any date from the existing start date if start is defined and in the past
                    : todayMidnight, // any date from today if no start date is defined
                }}
                onChange={(date: Date[]) =>
                  onChange({ ...meeting, start: date[0].toISOString() })
                }
              />
            </Form.Group>
          </Col>
          <Col md="4">
            <Form.Group>
              <Form.Label>{t("invitation.end")} *</Form.Label>
              <Flatpickr
                required
                disabled={!editMode}
                className="form-control"
                data-enable-time
                placeholder="YYYY-MM-DD HH:MM"
                value={meeting.end ? new Date(meeting.end) : undefined}
                options={{
                  minDate: meeting.start ? new Date(meeting.start) : new Date(),
                  // Limit the selectable end date to 5 days after the chosen start (only in edit mode for backwards compat)
                  maxDate:
                    editMode && meeting.start
                      ? moment(meeting.start).add(5, "days").toDate()
                      : undefined,
                  dateFormat: "Y-m-d H:i",
                  allowInput: true,
                }}
                onChange={(date: Date[]) =>
                  onChange({ ...meeting, end: date[0].toISOString() })
                }
              />
            </Form.Group>
          </Col>
        </Row>
        <Row hidden={!editMode}>
          <Col>
            <Badge variant="info">{t("important")}</Badge>{" "}
            <Trans i18nKey="invitation.max_length_notice">
              <a
                href={t("invitation.security_card_link")}
                target="_blank"
                rel="noreferrer"
              >
                &nbsp;
              </a>
              <a href="mailto:dw_575_MFTBC-SecurityCard@archion-group.com">
                &nbsp;
              </a>
            </Trans>
          </Col>
        </Row>
      </Col>
    </Row>
  );
};

export default MeetingInfoForm;
